import React from 'react';
import { Button, Modal } from 'antd';
import type { AppUpdateStatusPayload } from '../../../common/types/appUpdate';
import updateDialogBg from '../../assets/images/update-dialog-bg.png';
import updateRocket from '../../assets/images/update-rocket.png';

interface UpdateModalProps {
  effectiveUpdateStatus: AppUpdateStatusPayload | null;
  pendingUpdateStatus: AppUpdateStatusPayload | null;
  updateModalOpen: boolean;
  setUpdateModalOpen: (open: boolean) => void;
  updateActionSubmitting: boolean;
  handleUpdatePrimaryAction: () => Promise<void>;
}

const renderSingleVersionNotes = (releaseNotes: string) => {
  const notes = releaseNotes
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);

  return notes.map((item, index) => (
    <p key={`${item}-${index}`} className="main-layout__update-modal-note">{item}</p>
  ));
};

const renderUpdateModalNotes = (effectiveUpdateStatus: AppUpdateStatusPayload) => {
  const { releaseNotesList } = effectiveUpdateStatus;

  // 多版本：按版本分组展示
  if (releaseNotesList && releaseNotesList.length > 0) {
    return (
      <div className="main-layout__update-modal-notes">
        {releaseNotesList.map((entry, idx) => (
          <div key={`${entry.version}-${entry.buildVersion}`} className="main-layout__update-modal-version-group">
            {(releaseNotesList.length > 1 || idx === 0) && (
              <p className="main-layout__update-modal-version-title">V{entry.version}</p>
            )}
            {renderSingleVersionNotes(entry.releaseNotes)}
          </div>
        ))}
      </div>
    );
  }

  // 回退：使用单条 releaseNotes
  const notes = effectiveUpdateStatus.releaseNotes
    ?.split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);

  if (!notes || notes.length === 0) {
    return (
      <div className="main-layout__update-modal-notes">
        <p className="main-layout__update-modal-note">优化更新安装体验与稳定性。</p>
        <p className="main-layout__update-modal-note">修复部分已知问题，提升使用流畅度。</p>
        <p className="main-layout__update-modal-note">建议尽快完成升级以获得更完整的功能支持。</p>
      </div>
    );
  }

  return (
    <div className="main-layout__update-modal-notes">
      {notes.map((item, index) => (
        <p key={`${item}-${index}`} className="main-layout__update-modal-note">{item}</p>
      ))}
    </div>
  );
};

export const UpdateModal: React.FC<UpdateModalProps> = ({
  effectiveUpdateStatus,
  pendingUpdateStatus,
  updateModalOpen,
  setUpdateModalOpen,
  updateActionSubmitting,
  handleUpdatePrimaryAction,
}) => {
  if (!effectiveUpdateStatus) {
    return null;
  }

  const isForceUpdate = effectiveUpdateStatus.forceUpdate === true;

  return (
    <Modal
      open={updateModalOpen}
      centered
      closable={false}
      keyboard={!isForceUpdate && !updateActionSubmitting}
      footer={null}
      width={391}
      destroyOnHidden
      maskClosable={!isForceUpdate && !updateActionSubmitting}
      onCancel={() => {
        if (!isForceUpdate && !updateActionSubmitting) {
          setUpdateModalOpen(false);
        }
      }}
      className="main-layout__update-modal"
      styles={{ mask: { backgroundColor: 'rgba(0, 0, 0, 0.4)' } }}
    >
      <div
        className="main-layout__update-modal-shell"
        style={{ backgroundImage: `url(${updateDialogBg})` }}
      >
        <div className="main-layout__update-modal-body">
          <div className="main-layout__update-modal-hero" aria-hidden="true">
            <img src={updateRocket} alt="" className="main-layout__update-modal-hero-image" />
          </div>
          <h3 className="main-layout__update-modal-title">发现新版本</h3>
          <p className="main-layout__update-modal-version">
            {effectiveUpdateStatus.latestVersion ? `V${effectiveUpdateStatus.latestVersion}` : '新版本已就绪'}
          </p>
          <div className="main-layout__update-modal-section">
            <p className="main-layout__update-modal-section-title">更新内容：</p>
            {renderUpdateModalNotes(effectiveUpdateStatus)}
          </div>
          <div className="main-layout__update-modal-actions">
            {!isForceUpdate ? (
              <Button
                className="main-layout__update-modal-cancel"
                onClick={() => setUpdateModalOpen(false)}
                disabled={updateActionSubmitting}
              >
                取消
              </Button>
            ) : null}
            <Button
              type="primary"
              className="main-layout__update-modal-submit"
              loading={updateActionSubmitting}
              onClick={() => void handleUpdatePrimaryAction()}
            >
              {pendingUpdateStatus?.status === 'downloaded' ? '重启更新' : '立即更新'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
